import { useState, useRef, useCallback } from 'react';
import { removeBackground } from '../services/api';
import { saveImage } from '../utils/saveImage';

export type BgRemovalStatus = 'idle' | 'processing' | 'done' | 'error';

export function useBgRemoval() {
  const [status, setStatus] = useState<BgRemovalStatus>('idle');
  const [originalUrl, setOriginalUrl] = useState<string | null>(null);
  const [resultBlob, setResultBlob] = useState<Blob | null>(null);
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  /** 執行去背 */
  const process = useCallback(async (file: File) => {
    // 清理前一次的結果
    if (resultUrl) URL.revokeObjectURL(resultUrl);

    setStatus('processing');
    setErrorMsg(null);
    setResultBlob(null);
    setResultUrl(null);

    // 顯示原圖預覽
    const origUrl = URL.createObjectURL(file);
    setOriginalUrl(origUrl);

    abortRef.current = new AbortController();

    try {
      const blob = await removeBackground(file, abortRef.current.signal);
      const url = URL.createObjectURL(blob);
      setResultBlob(blob);
      setResultUrl(url);
      setStatus('done');
    } catch (e: any) {
      if (e.name === 'AbortError') {
        setStatus('idle');
      } else {
        setErrorMsg(e.message || '去背失敗');
        setStatus('error');
      }
    }
  }, [resultUrl]);

  /** 取消進行中的去背 */
  const cancel = useCallback(() => {
    abortRef.current?.abort();
    setStatus('idle');
  }, []);

  /** 重置狀態 */
  const reset = useCallback(() => {
    abortRef.current?.abort();
    if (resultUrl) URL.revokeObjectURL(resultUrl);
    if (originalUrl) URL.revokeObjectURL(originalUrl);
    setStatus('idle');
    setOriginalUrl(null);
    setResultBlob(null);
    setResultUrl(null);
    setErrorMsg(null);
  }, [resultUrl, originalUrl]);

  /** 儲存去背結果 PNG。iOS 走系統分享表單（可存進照片庫），其餘平台直接下載 */
  const save = useCallback(async () => {
    // 用 resultBlob 而非 resultUrl：saveImage 需要原始 blob 才能包成 File 交給分享表單
    if (!resultBlob) return;
    await saveImage(resultBlob, `removed-bg-${Date.now()}.png`, '去背圖片');
  }, [resultBlob]);

  return {
    status,
    originalUrl,
    resultBlob,
    resultUrl,
    errorMsg,
    process,
    cancel,
    reset,
    save,
  };
}
